import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import { toast } from "sonner";
import {
  Loader2, Calendar, Clock, User, Mail, BookOpen,
  CheckCircle2, XCircle, ChevronDown, CalendarCheck, StickyNote,
} from "lucide-react";

const STATUSES = [
  { value: "all",       label: "All",       colour: "#64748b" },
  { value: "pending",   label: "Pending",   colour: "#d97706" },
  { value: "confirmed", label: "Confirmed", colour: "#2563eb" },
  { value: "completed", label: "Completed", colour: "#16a34a" },
  { value: "cancelled", label: "Cancelled", colour: "#dc2626" },
];

const STATUS_META = {
  pending:   { label: "Pending",   bg: "bg-amber-50",  text: "text-amber-700",  border: "border-amber-200" },
  confirmed: { label: "Confirmed", bg: "bg-blue-50",   text: "text-blue-700",   border: "border-blue-200"  },
  completed: { label: "Completed", bg: "bg-green-50",  text: "text-green-700",  border: "border-green-200" },
  cancelled: { label: "Cancelled", bg: "bg-red-50",    text: "text-red-700",    border: "border-red-200"   },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${m.bg} ${m.text} ${m.border}`}>
      {m.label}
    </span>
  );
}

function ActionMenu({ booking, onStatusChange, updating }) {
  const [open, setOpen] = useState(false);
  const actions = [
    { status: "confirmed", label: "Confirm",          icon: CheckCircle2, colour: "#2563eb" },
    { status: "completed", label: "Mark Completed",   icon: CalendarCheck, colour: "#16a34a" },
    { status: "cancelled", label: "Cancel",           icon: XCircle,      colour: "#dc2626" },
    { status: "pending",   label: "Reset to Pending", icon: Clock,        colour: "#d97706" },
  ].filter(a => a.status !== booking.status);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        disabled={updating}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
      >
        {updating ? <Loader2 size={12} className="animate-spin" /> : "Actions"}
        <ChevronDown size={12} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[190px]">
            {actions.map(({ status, label, icon: Icon, colour }) => (
              <button
                key={status}
                onClick={() => { setOpen(false); onStatusChange(booking.id, status); }}
                className="flex items-center gap-2.5 w-full px-4 py-2.5 text-xs hover:bg-slate-50 transition-colors text-left"
                style={{ color: colour }}
              >
                <Icon size={13} />
                {label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function BookingCard({ booking, onStatusChange, updating }) {
  const isPast = new Date(booking.date) < new Date(new Date().toISOString().slice(0, 10));
  const dateFormatted = new Date(booking.date + "T00:00:00").toLocaleDateString("en-GB", {
    weekday: "short", day: "numeric", month: "short", year: "numeric",
  });

  return (
    <div className={`bg-white border rounded-xl p-5 hover:shadow-sm transition-shadow ${isPast && booking.status === "pending" ? "border-amber-300" : "border-slate-200"}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-1.5 bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">
              <Calendar size={11} />
              {dateFormatted}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-600 bg-slate-100 px-2.5 py-1.5 rounded-lg">
              <Clock size={11} className="text-slate-400" />
              {booking.time_slot}
            </div>
            <span className="text-xs text-slate-500 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-lg font-medium">
              {booking.lesson_type}
            </span>
            <StatusBadge status={booking.status} />
            {isPast && booking.status === "pending" && (
              <span className="text-[10px] font-bold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full uppercase tracking-wide">
                Overdue
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5 text-xs text-slate-600">
            <div className="flex items-center gap-1.5">
              <User size={11} className="text-slate-400 shrink-0" />
              <span className="font-medium text-slate-800">{booking.student_name}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Mail size={11} className="text-slate-400 shrink-0" />
              <a href={`mailto:${booking.student_email}`} className="hover:text-blue-600 truncate">
                {booking.student_email}
              </a>
            </div>
            {booking.topic && (
              <div className="flex items-center gap-1.5">
                <BookOpen size={11} className="text-slate-400 shrink-0" />
                {booking.topic}
              </div>
            )}
          </div>

          {booking.notes && (
            <div className="mt-2.5 flex items-start gap-1.5 text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
              <StickyNote size={11} className="text-slate-400 shrink-0 mt-0.5" />
              <span className="italic">"{booking.notes}"</span>
            </div>
          )}
        </div>

        <ActionMenu booking={booking} onStatusChange={onStatusChange} updating={updating === booking.id} />
      </div>
    </div>
  );
}

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [stats, setStats] = useState({});

  const fetchBookings = async (status) => {
    setLoading(true);
    try {
      const [bRes, statsRes] = await Promise.all([
        api.get(`/admin/bookings?status=${status}`),
        api.get("/admin/stats"),
      ]);
      setBookings(bRes.data.bookings);
      setStats(statsRes.data);
    } catch {
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBookings(filter); }, [filter]);

  const handleStatusChange = async (id, status) => {
    setUpdating(id);
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success(`Booking ${status}`);
      fetchBookings(filter);
    } catch {
      toast.error("Failed to update booking");
    } finally {
      setUpdating(null);
    }
  };

  return (
    <AdminShell title="Bookings" subtitle="Manage lesson booking requests" stats={stats}>
      <div className="flex gap-2 mb-6 flex-wrap">
        {STATUSES.map(s => (
          <button
            key={s.value}
            onClick={() => setFilter(s.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
              filter === s.value ? "text-white border-transparent" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}
            style={filter === s.value ? { background: s.colour, borderColor: s.colour } : {}}
          >
            {s.label}
            {s.value === "pending" && stats.pending_bookings > 0 && (
              <span className="ml-1.5 bg-white/30 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                {stats.pending_bookings}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 size={28} className="animate-spin text-slate-400" />
        </div>
      ) : bookings.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
          <CalendarCheck size={36} className="mx-auto text-green-400 mb-3" />
          <div className="font-medium text-slate-700">All clear!</div>
          <div className="text-sm text-slate-400 mt-1">No {filter !== "all" ? filter : ""} bookings right now.</div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-xs text-slate-400 mb-2">{bookings.length} booking{bookings.length !== 1 ? "s" : ""}</div>
          {bookings.map(b => (
            <BookingCard key={b.id} booking={b} onStatusChange={handleStatusChange} updating={updating} />
          ))}
        </div>
      )}
    </AdminShell>
  );
}
